import { memo, type ReactNode } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Paper,
  Button,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  DragIndicator as DragIndicatorIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  FolderSpecial as SubmenuIcon,
} from "@mui/icons-material";
import { useMenuLevel } from "./useMenuLevel";
import { MenuButton, CustomKeyboardLayout } from "@bale-bot/types";
import { asRows } from "./menuHelpers";

interface MenuLevelProps {
  rows: MenuButton[][];
  onChange: (rows: MenuButton[][]) => void;
  depth: number;
  keyPrefix: string;
}

interface MenuBuilderProps {
  layout: CustomKeyboardLayout;
  onChange: (newLayout: CustomKeyboardLayout) => void;
}

interface MenuLevelProps {
  rows: MenuButton[][];
  onChange: (rows: MenuButton[][]) => void;
  /** 0 for the root menu; deeper levels are rendered inside their folder. */
  depth: number;
  keyPrefix: string;
}

/**
 * One menu level: rows of buttons, each of which can become a folder holding
 * another level. Recursive, so any depth works with no special-casing. Only the
 * root level is drag-and-drop sortable.
 *
 * Memoized: an edit only produces new arrays along the path that changed, so
 * untouched branches keep their props and skip re-rendering.
 */
const MenuLevel = memo(function MenuLevel({
  rows,
  onChange,
  depth,
  keyPrefix,
}: MenuLevelProps) {
  const {
    isRoot,
    handleDragEnd,
    handleAddRow,
    handleDeleteRow,
    handleAddButton,
    handleDeleteButton,
    handleUpdateButtonText,
    handleUpdateButtonAction,
    handleToggleSubmenu,
    handleSubmenuChange,
    getButtonMeta: _getButtonMeta,
  } = useMenuLevel({ rows, onChange, depth });

  const renderButton = (
    button: MenuButton,
    rowIndex: number,
    buttonIndex: number,
  ) => {
    const isFolder = button.submenu !== undefined;
    const actionValue = button.url ?? button.callback_data ?? "";
    const submenuRows = asRows(button.submenu);

    return (
      <Paper
        key={`${keyPrefix}btn-${rowIndex}-${buttonIndex}`}
        variant="outlined"
        sx={{ p: 2, bgcolor: "background.default" }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: "center" }}
          >
            <TextField
              label="Button Text"
              value={button.text}
              onChange={(e) =>
                handleUpdateButtonText(rowIndex, buttonIndex, e.target.value)
              }
              size="small"
              fullWidth
              required
            />

            <TextField
              label={
                isFolder ? "Action (Folder Active)" : "URL or Callback Data"
              }
              value={isFolder ? "" : actionValue}
              placeholder={
                isFolder
                  ? "Disabled for submenus"
                  : "http://... or callback_string"
              }
              onChange={(e) =>
                handleUpdateButtonAction(rowIndex, buttonIndex, e.target.value)
              }
              disabled={isFolder}
              size="small"
              fullWidth
              helperText={
                isFolder
                  ? "Submenu buttons act as folders"
                  : button.url
                    ? "Type: URL"
                    : "Type: Callback Data"
              }
            />

            <Stack direction="row" spacing={1} sx={{ shrink: 0 }}>
              <Tooltip
                title={
                  isFolder ? "Remove Submenu" : "Convert to Submenu Folder"
                }
              >
                <IconButton
                  color={isFolder ? "primary" : "default"}
                  onClick={() => handleToggleSubmenu(rowIndex, buttonIndex)}
                >
                  <SubmenuIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Button">
                <IconButton
                  color="error"
                  onClick={() => handleDeleteButton(rowIndex, buttonIndex)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {isFolder && (
            <Accordion defaultExpanded variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                  Submenu Rows ({submenuRows.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MenuLevel
                  rows={submenuRows}
                  onChange={(submenu) =>
                    handleSubmenuChange(rowIndex, buttonIndex, submenu)
                  }
                  depth={depth + 1}
                  keyPrefix={`${keyPrefix}${rowIndex}-${buttonIndex}-`}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderRowBody = (
    row: MenuButton[],
    rowIndex: number,
    dragHandle?: ReactNode,
  ) => (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
          {dragHandle}
          <Typography variant="subtitle2" color="text.secondary">
            Row {rowIndex + 1}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddButton(rowIndex)}
          >
            Add Button
          </Button>
          <Tooltip title="Delete Row">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteRow(rowIndex)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing={2}>
        {row.map((button: MenuButton, buttonIndex: number) =>
          renderButton(button, rowIndex, buttonIndex),
        )}
      </Stack>
    </Stack>
  );

  const addRowButton = (
    <Button
      variant={isRoot ? "contained" : "text"}
      size={isRoot ? "medium" : "small"}
      startIcon={<AddIcon />}
      onClick={handleAddRow}
      sx={{ alignSelf: "flex-start" }}
    >
      Add New Row
    </Button>
  );

  // Nested levels: same row layout, without drag-and-drop.
  if (!isRoot) {
    return (
      <Stack spacing={2}>
        {rows.map((row, rowIndex) => (
          <Paper
            key={`${keyPrefix}row-${rowIndex}`}
            variant="outlined"
            sx={{ p: 2 }}
          >
            {renderRowBody(row, rowIndex)}
          </Paper>
        ))}
        {addRowButton}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="menu-builder-rows">
          {(droppableProvided) => (
            <Box
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              {rows.map((row, rowIndex) => (
                <Draggable
                  key={`row-${rowIndex}`}
                  draggableId={`row-${rowIndex}`}
                  index={rowIndex}
                >
                  {(draggableProvided, snapshot) => (
                    <Paper
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      elevation={snapshot.isDragging ? 6 : 2}
                      sx={{
                        p: 2,
                        backgroundColor: snapshot.isDragging
                          ? "action.hover"
                          : "background.paper",
                        transition: "box-shadow 0.2s, background-color 0.2s",
                      }}
                    >
                      {renderRowBody(
                        row,
                        rowIndex,
                        <Box
                          {...draggableProvided.dragHandleProps}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "grab",
                            color: "text.secondary",
                            "&:active": { cursor: "grabbing" },
                          }}
                        >
                          <Tooltip title="Drag to reorder row">
                            <DragIndicatorIcon />
                          </Tooltip>
                        </Box>,
                      )}
                    </Paper>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {addRowButton}
    </Stack>
  );
});
export default function MenuBuilder({ layout, onChange }: MenuBuilderProps) {
  return (
    <Stack
      direction={"row"}
      spacing={2}
      sx={{ width: "100%", mx: "auto", p: 2 }}
    >
      <Paper>
        <MenuLevel rows={layout} onChange={onChange} depth={0} keyPrefix="" />
      </Paper>
    </Stack>
  );
}